import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { gpsService, type GpsFix, type GpsPermissionState } from '@/services/gps';

/**
 * Shows live GPS status AND the actual coordinates being captured, so the driver
 * (and we) can verify real fixes are flowing — not just a "ready" label.
 */
export function GpsStatusLine() {
  const [state, setState] = useState<GpsPermissionState>('checking');
  const [fix, setFix] = useState<GpsFix | null>(null);

  const refresh = useCallback(async (fromGesture = false) => {
    if (!gpsService.getCachedFix()) setState('checking');
    if (fromGesture) {
      const f = await gpsService.warmUp();
      setState(f ? 'ready' : await gpsService.checkStatus());
    } else {
      setState(await gpsService.checkStatus());
    }
  }, []);

  useEffect(() => {
    // Live subscription: flip to "ready" and show coordinates the instant a fix
    // arrives, even if the driver grants permission seconds after load.
    const unsubscribe = gpsService.subscribe((f) => {
      setFix(f);
      setState('ready');
    });
    void refresh(false);
    return unsubscribe;
  }, [refresh]);

  if (state === 'ready' && fix) {
    return (
      <p className="text-sm text-brand-muted">
        GPS locked:{' '}
        <span className="font-semibold text-white">
          {fix.latitude.toFixed(5)}, {fix.longitude.toFixed(5)}
        </span>{' '}
        (±{Math.round(fix.accuracy)} m) — recorded on each action.
      </p>
    );
  }
  if (state === 'ready') {
    return <p className="text-sm text-brand-muted">GPS ready — location is recorded on each action.</p>;
  }
  if (state === 'checking') {
    return <p className="text-sm text-brand-muted">Getting GPS location…</p>;
  }
  if (state === 'denied') {
    return (
      <div className="space-y-2">
        <p className="text-sm text-brand-error">
          No GPS yet. Tap Get GPS and choose “Allow While Using App”. If nothing happens, check iPhone
          Settings → Safari → Location.
        </p>
        <Button variant="outline" size="sm" onClick={() => void refresh(true)}>
          Get GPS
        </Button>
      </div>
    );
  }
  // 'acquiring' or 'unavailable' — both recoverable; offer a gesture retry.
  return (
    <div className="space-y-2">
      <p className="text-sm text-brand-warn">
        Acquiring GPS — tap Get GPS and allow location when prompted (works best outdoors).
      </p>
      <Button variant="outline" size="sm" onClick={() => void refresh(true)}>
        Get GPS
      </Button>
    </div>
  );
}
